import { MeetingStatus } from "api/generated/schemas/meetingStatus";
import { MEETING_TYPES, type MeetingType } from "./meetingType.constants";

export const MEETING_TYPE_LABELS: Record<MeetingType, string> = {
  [MEETING_TYPES.MEETING]: "Bespreking",
  [MEETING_TYPES.GROUP_MEETING]: "Groepsbespreking",
  [MEETING_TYPES.TRANSITION_MEETING]: "Adviesbespreking",
} as const;

export const MEETING_TYPE_SECTION_LABELS: Record<MeetingType, string> = {
  [MEETING_TYPES.MEETING]: "Leerlingbesprekingen",
  [MEETING_TYPES.GROUP_MEETING]: "Groepsbesprekingen",
  [MEETING_TYPES.TRANSITION_MEETING]: "Adviesbesprekingen",
} as const;

/** Subset of CBadge palette colors (styles/modules/_tags.scss) used for meeting tags. */
export type MeetingDisplayTagColor =
  | "blue"
  | "green"
  | "yellow"
  | "purple"
  | "grey";

/** CBadge color for the type tag (rendered with the soft/`inverse` variant). */
export const MEETING_TYPE_TAG_COLORS: Record<
  MeetingType,
  MeetingDisplayTagColor
> = {
  [MEETING_TYPES.MEETING]: "green",
  [MEETING_TYPES.GROUP_MEETING]: "purple",
  [MEETING_TYPES.TRANSITION_MEETING]: "yellow",
} as const;

export const MEETING_TYPE_ORDER: MeetingType[] = [
  MEETING_TYPES.MEETING,
  MEETING_TYPES.GROUP_MEETING,
  MEETING_TYPES.TRANSITION_MEETING,
];

/**
 * Font Awesome icon shown in the leading square of a meeting row, per type.
 * Rendered with `toClassicIcon` (regular style) to match the design.
 */
export const MEETING_TYPE_ICONS: Record<MeetingType, string> = {
  [MEETING_TYPES.MEETING]: "fa-comment-dots",
  [MEETING_TYPES.GROUP_MEETING]: "fa-users",
  [MEETING_TYPES.TRANSITION_MEETING]: "fa-comment-medical",
} as const;

/**
 * Background (square) and text (icon) colors for the leading meeting-row icon,
 * per type.
 */
export const MEETING_TYPE_ICON_COLORS: Record<
  MeetingType,
  { bg: string; text: string }
> = {
  [MEETING_TYPES.MEETING]: { bg: "tw-bg-green-100", text: "tw-text-green-500" },
  [MEETING_TYPES.GROUP_MEETING]: {
    bg: "tw-bg-purple-100",
    text: "tw-text-purple-500",
  },
  [MEETING_TYPES.TRANSITION_MEETING]: {
    bg: "tw-bg-yellow-100",
    text: "tw-text-yellow-500",
  },
} as const;

export const MEETING_STATUS_LABELS: Record<MeetingStatus, string> = {
  [MeetingStatus.concept]: "Concept",
  [MeetingStatus.open]: "Open",
  [MeetingStatus.open_for_students]: "Open voor leerlingen",
  [MeetingStatus.closed_modify]: "Gesloten (bewerkbaar)",
  [MeetingStatus.closed_no_modify]: "Gesloten",
  [MeetingStatus.archived]: "Afgerond",
} as const;

/** CBadge color for the status tag (rendered with the soft/`inverse` variant). */
export const MEETING_STATUS_TAG_COLORS: Record<
  MeetingStatus,
  MeetingDisplayTagColor
> = {
  [MeetingStatus.concept]: "grey",
  [MeetingStatus.open]: "blue",
  [MeetingStatus.open_for_students]: "blue",
  [MeetingStatus.closed_modify]: "yellow",
  [MeetingStatus.closed_no_modify]: "yellow",
  [MeetingStatus.archived]: "green",
} as const;

/**
 * Statuses shown under the "Afgeronde besprekingen" tab. Only `archived` is
 * finished; a `closed_no_modify` meeting has locked ratings but is still
 * running (leerlingstoplicht delen, driehoeksgesprekken), so it stays under
 * "Actuele besprekingen". Mirrors the backend's COMPLETED_LIST_STATUSES.
 */
export const COMPLETED_MEETING_STATUSES: MeetingStatus[] = [
  MeetingStatus.archived,
];
